import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps the free-postage progress bar accurate after an AJAX cart change.
 *
 * The bar is rendered by Liquid on every page load, so the first paint is always
 * correct and it degrades to a still-accurate static bar if this module never
 * loads. This component exists only to cover the gap Liquid cannot: adding a card
 * through the cart drawer updates the drawer, but leaves the page behind it
 * untouched, so without this the sticky bar would show a stale figure until the
 * visitor navigated.
 *
 * @extends {Component}
 */
class PostageProgressComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  /**
   * The event fires as the request goes out, so wait on its promise where one is
   * offered — otherwise we would read the cart before the line was applied.
   * @param {any} event
   */
  #handleCartLinesUpdate = (event) => {
    const settle = () => this.#refresh();
    if (event?.promise?.then) {
      event.promise.then(settle).catch(settle);
    } else {
      settle();
    }
  };

  async #refresh() {
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      this.#paint(Number(cart.total_price) || 0);
    } catch (error) {
      // Leave the server-rendered figure in place rather than showing a wrong one.
    }
  }

  /** @param {number} total */
  #paint(total) {
    const threshold = Number(this.dataset.threshold) || 0;
    if (!threshold) return;

    const pct = Math.min(100, Math.floor((total * 100) / threshold));
    const remaining = Math.max(0, threshold - total);

    const fill = this.querySelector('[data-pp-fill]');
    const track = this.querySelector('[data-pp-track]');
    const label = this.querySelector('[data-pp-label]');

    if (fill instanceof HTMLElement) fill.style.width = `${pct}%`;
    if (track) track.setAttribute('aria-valuenow', String(pct));
    if (!label) return;

    if (remaining === 0) {
      label.textContent = this.dataset.metText ?? '';
      label.classList.add('postage-progress__label--met');
      return;
    }

    label.classList.remove('postage-progress__label--met');
    const symbol = this.dataset.currencySymbol ?? '$';
    const amount = `${symbol}${(remaining / 100).toFixed(2)}`;
    label.innerHTML = `<span class="postage-progress__amount"></span> ${this.dataset.toGoText ?? ''}`;
    const amountEl = label.querySelector('.postage-progress__amount');
    if (amountEl) amountEl.textContent = amount;
  }
}

if (!customElements.get('postage-progress-component')) {
  customElements.define('postage-progress-component', PostageProgressComponent);
}
